/**
 * Role utility functions for checking user permissions and roles
 */

import { ROUTES } from '../constants/routes';

/**
 * Get all role slugs from an operator object
 * @param {Object|null} operator - Operator object with roles array
 * @returns {Array<string>} Array of role slugs
 */
export const getRoleSlugs = (operator) => {
  if (!operator) return [];
  let slugs = [];
  if (Array.isArray(operator.roles)) {
    slugs = operator.roles
      .map((r) => (typeof r === 'string' ? r : r?.slug))
      .filter(Boolean);
  } else if (typeof operator.role === 'string') {
    slugs = [operator.role];
  } else if (typeof operator.role_slug === 'string') {
    slugs = [operator.role_slug];
  }
  if (operator.is_admin || operator.is_superuser) {
    if (!slugs.includes('admin')) slugs.push('admin');
  }
  return slugs;
};

/**
 * Check if operator has a specific role
 * @param {Object|null} operator - Operator object
 * @param {string|Array<string>} roleSlug - Single role slug or array of role slugs
 * @returns {boolean} True if operator has the role(s)
 */
export const hasRole = (operator, roleSlug) => {
  const roles = getRoleSlugs(operator);
  if (Array.isArray(roleSlug)) {
    return roleSlug.some((slug) => roles.includes(slug));
  }
  return roles.includes(roleSlug);
};

/**
 * Check if operator is admin
 * @param {Object|null} operator - Operator object
 * @returns {boolean} True if operator is admin
 */
export const isAdmin = (operator) => {
  return hasRole(operator, 'admin');
};

/**
 * Check if the given operator can edit a specific log entry.
 * - Admin: always yes (any log, any date, any operator)
 * - Operator: only their own logs from today
 * @param {Object|null} operator
 * @param {Object} log - The log record from the API
 * @returns {boolean}
 */
export const canEditLog = (operator, log) => {
  if (!operator || !log) return false;
  if (isAdmin(operator)) return true;
  const today = new Date().toISOString().slice(0, 10);
  const logDate = log.log_date || '';
  const isToday = logDate === today;
  const isOwn =
    log.operator === operator.id ||
    log.operator_name === operator.name ||
    log.created_by === operator.id;
  return isToday && isOwn;
};


/**
 * Get role permissions object for an operator
 * @param {Object|null} operator - Operator object
 * @returns {Object} Object with boolean flags for each role
 */
export const getRolePermissions = (operator) => {
  const roles = getRoleSlugs(operator);
  const admin = roles.includes('admin') || Boolean(operator?.is_admin) || Boolean(operator?.is_superuser);
  
  return {
    isAdmin: admin,
    hasConsumptionRole: admin || roles.includes('consumption'),
    hasConsumptionCostRole: admin || roles.includes('consumption_cost'),
    hasElectricityConsumptionRole: admin || roles.includes('electricity_consumption'),
    hasQualityRole: admin || roles.includes('quality') || roles.includes('test_certificate'),
    hasExtrusionRole: admin || roles.includes('extrusion'),
    hasRollingRole: admin || roles.includes('rolling'),
    hasDrawRole: admin || roles.includes('draw'),
    hasDispatchRole: admin || roles.includes('dispatch'),
    hasDayWorkingRole: admin || roles.includes('day_working'),
    hasVMCRole: admin || roles.includes('vmc'),
    hasPackingRole: admin || roles.includes('packing'),
    hasRawMaterialRole:
      admin ||
      roles.includes('raw_material') ||
      roles.includes('furnace') ||
      roles.includes('order') ||
      roles.includes('purchase_order') ||
      roles.includes('procurement') ||
      roles.includes('jobwork') ||
      roles.includes('store') ||
      roles.includes('production') ||
      roles.includes('dispatch'),
    hasJobworkRole: admin || roles.includes('raw_material') || roles.includes('dispatch') || roles.includes('jobwork'),
    hasFurnaceRole: admin || roles.includes('furnace'),
    hasOrderRole: admin || roles.includes('order'),
    hasPurchaseOrderRole: admin || roles.includes('purchase_order'),
    hasScrapRole: admin || roles.includes('scrap'),
    hasInternalRejectionRole: admin || roles.includes('internal_rejection'),
    hasDiePlannerRole: admin || roles.includes('die_planner'),
    hasProcurementRole: admin || roles.includes('procurement'),
    hasMaintenanceRole: admin || roles.includes('maintenance') || roles.includes('maintenance_admin'),
    hasMaintenanceMechanicalRole: admin || roles.includes('maintenance_mechanical'),
    hasMaintenanceElectricalRole: admin || roles.includes('maintenance_electrical'),
    hasAiAssistantRole:
      admin ||
      roles.includes('order') ||
      roles.includes('purchase_order') ||
      roles.includes('maintenance') ||
      roles.includes('maintenance_admin'),
    hasProductionTrackerRole: admin || roles.includes('production_tracker') || roles.includes('order'),
    hasProductionDashboardRole:
      admin ||
      roles.includes('extrusion') ||
      roles.includes('draw') ||
      roles.includes('rolling') ||
      roles.includes('vmc') ||
      roles.includes('production') ||
      roles.includes('production_tracker') ||
      roles.includes('order') ||
      roles.includes('day_working') ||
      roles.includes('furnace'),
  };
};

/**
 * Get the default route for an operator based on their roles
 * @param {Object|null} operator - Operator object
 * @returns {string|null} Default route path or null
 */
export const getDefaultRoute = (operator) => {
  if (!operator) return null;
  
  const permissions = getRolePermissions(operator);
  const roles = getRoleSlugs(operator);
  
  // Priority order for default routes
  if (permissions.hasScrapRole) return ROUTES.SCRAP;
  if (permissions.hasInternalRejectionRole) return ROUTES.INTERNAL_REJECTION;
  if (permissions.hasPackingRole) return ROUTES.PACKING_NEW;
  if (permissions.hasDispatchRole) return ROUTES.DISPATCH_NEW;
  if (permissions.hasQualityRole) return ROUTES.QUALITY;
  if (permissions.hasExtrusionRole) return ROUTES.EXTRUSION;
  if (permissions.hasRollingRole) return ROUTES.ROLLING;
  if (permissions.hasDrawRole) return ROUTES.DRAW;
  if (permissions.hasDayWorkingRole) return ROUTES.DAY_WORKING;
  if (permissions.hasVMCRole) return ROUTES.VMC;
  if (permissions.hasConsumptionRole || permissions.hasConsumptionCostRole) return ROUTES.CONSUMPTION;
  if (permissions.hasElectricityConsumptionRole) return ROUTES.ELECTRICITY_CONSUMPTION;
  if (permissions.hasOrderRole) return ROUTES.ORDERS;
  if (permissions.hasProcurementRole) return ROUTES.PROCUREMENT;
  if (permissions.hasMaintenanceRole) return ROUTES.MAINTENANCE;
  if (permissions.hasMaintenanceMechanicalRole || permissions.hasMaintenanceElectricalRole) {
    return ROUTES.MAINTENANCE_PLANNER;
  }
  
  return null;
};
